import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cx } from "./util";

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: ReactNode;
  /** Rendered under the input, tied to the input via aria-describedby. */
  hint?: ReactNode;
  /** Loud and specific. Announced politely, not on every keystroke. */
  error?: string | null;
  /** Room-code treatment: Arial Black, tracked out, centred. */
  code?: boolean;
}

export function Field({
  label,
  hint,
  error,
  code = false,
  className,
  id,
  ...rest
}: FieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cx("bd-field", error && "bd-shake")}>
      <label className="field-label t-label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        className={cx("field", code && "field-code", className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {hint ? (
        <p id={hintId} className="t-micro muted bd-field-note">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="t-micro danger bd-field-note" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
